// Central content for The Tech Savvy Company

export const CONTACT = {
  phone: "(380) 282-2525",
  phoneHref: "tel:+13802822525",
  location: "Columbus, Ohio",
  email: "Lawrence@thetechsavvyco.com",
  hours: "Mon–Sat · 8am–7pm",
};

export const SERVICES = [
  {
    slug: "phone-setup",
    icon: "Smartphone",
    title: "New Phone Setup & Data Transfer",
    short: "Get set up on your new phone with all your photos, contacts, and apps moved over safely.",
    points: [
      "Move photos, contacts, texts & apps from your old phone",
      "Set up email, messaging, and your favorite apps",
      "Show you the basics at your own pace",
    ],
  },
  {
    slug: "computer-setup",
    icon: "Monitor",
    title: "Computer & Laptop Setup",
    short: "We unbox, set up, and get your new computer ready so it just works.",
    points: [
      "Unboxing & first-time setup",
      "Transfer files from an old computer",
      "Install printers, updates & everyday programs",
    ],
  },
  {
    slug: "wifi-network",
    icon: "Wifi",
    title: "Wi‑Fi & Home Network Setup",
    short: "Strong, reliable internet in every room of your home.",
    points: [
      "Router & modem setup",
      "Fix weak signal & dead zones",
      "Secure your network with a strong password",
    ],
  },
  {
    slug: "smart-home",
    icon: "Camera",
    title: "Ring Camera & Smart Home Install",
    short: "Doorbells, cameras, speakers, and smart devices installed and explained.",
    points: [
      "Ring doorbell & security camera setup",
      "Smart speakers, plugs & thermostats",
      "Connect everything to your phone",
    ],
  },
  {
    slug: "printer-setup",
    icon: "Printer",
    title: "Printer Setup",
    short: "Wireless printing from your phone, tablet, or computer.",
    points: [
      "Wireless & USB printer setup",
      "Connect printing from all your devices",
      "Fix jams, ink & scanning issues",
    ],
  },
  {
    slug: "email-passwords",
    icon: "KeyRound",
    title: "Email & Password Help",
    short: "Regain access, organize your inbox, and keep your accounts safe.",
    points: [
      "Recover locked accounts",
      "Set up & organize email",
      "Safely store & manage passwords",
    ],
  },
  {
    slug: "tech-lessons",
    icon: "GraduationCap",
    title: "Tech Lessons & 1‑on‑1 Coaching",
    short: "Patient, friendly lessons in plain English — at your speed.",
    points: [
      "Learn video calls with family",
      "Photos, texting & everyday apps",
      "Ask any question — no judgement",
    ],
  },
];

export const TESTIMONIALS = [
  {
    name: "Margaret W.",
    role: "Retired teacher · Bexley",
    quote:
      "I was nervous about my new iPhone, but they were so patient. Everything from my old phone is here and they showed me how to video call my grandkids!",
    rating: 5,
  },
  {
    name: "Robert & Linda H.",
    role: "Homeowners · Dublin",
    quote:
      "Our Wi‑Fi never reached the back bedroom. Now it works everywhere and they explained it all without any confusing tech talk.",
    rating: 5,
  },
  {
    name: "The Chen Family",
    role: "Set up devices for Grandpa · Columbus",
    quote:
      "We hired them to set up a tablet for my father. They were respectful, kind, and he actually uses it every day now. Worth every penny.",
    rating: 5,
  },
  {
    name: "Dorothy P.",
    role: "First-time smartphone user",
    quote:
      "I finally understand my phone. They wrote down simple steps for me and told me I could call anytime. Truly friendly people.",
    rating: 5,
  },
  {
    name: "James T.",
    role: "Ring doorbell install · Grove City",
    quote:
      "They installed my Ring doorbell and two cameras, then set it all up on my phone. Quick, clean, and easy to follow.",
    rating: 5,
  },
  {
    name: "Susan M.",
    role: "Email recovery",
    quote:
      "I was locked out of my email for weeks. They got me back in within the hour and organized my inbox. Such a relief!",
    rating: 5,
  },
];

export const FAQS = [
  {
    q: "Do you come to my home?",
    a: "Yes! We offer in-home visits throughout the Columbus, Ohio area. We can also help over the phone or by video call for simple questions.",
  },
  {
    q: "I'm not good with technology. Will you explain things simply?",
    a: "Absolutely. That's our whole purpose. We speak in plain English, go at your pace, and are always happy to repeat anything. There are no silly questions here.",
  },
  {
    q: "How much do your services cost?",
    a: "Every situation is a little different, so we give you a clear, upfront price before we start — no surprises. Call us at (380) 282-2525 for a friendly, no-pressure quote.",
  },
  {
    q: "Do you help seniors specifically?",
    a: "Yes, helping seniors is one of the things we love most. We are patient, respectful, and take the time to make sure you feel comfortable and confident.",
  },
  {
    q: "What devices do you work with?",
    a: "iPhones, Androids, iPads, tablets, Windows and Mac computers, printers, Wi‑Fi routers, Ring cameras, smart speakers, and most everyday home technology.",
  },
  {
    q: "What if I have a problem after you leave?",
    a: "Just give us a call. We're always happy to answer follow-up questions and make sure everything keeps working the way it should.",
  },
];

export const BLOG_POSTS = [
  {
    slug: "5-phone-settings-for-seniors",
    title: "5 Phone Settings That Make Your Smartphone Easier to Use",
    excerpt:
      "Simple changes that make text bigger, sound louder, and your phone far easier on the eyes.",
    date: "June 2, 2026",
    category: "Smartphones",
    image:
      "https://images.unsplash.com/photo-1758691030826-86a149b6278b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    body: [
      "Your smartphone can be adjusted to fit you — not the other way around. Here are five friendly changes that make a real difference.",
      "1. Make the text bigger. In your settings, look for 'Display' and then 'Text Size'. Slide it up until reading feels comfortable.",
      "2. Turn up the ringer and add vibration. This makes calls and messages much easier to notice.",
      "3. Enable 'Bold Text' for clearer letters across every app.",
      "4. Set up 'Do Not Disturb' at night so notifications don't wake you.",
      "5. Add your favorite people to Favorites so calling family is just one tap away.",
      "Need a hand with any of these? We're always happy to sit down and walk through them with you.",
    ],
  },
  {
    slug: "keeping-passwords-safe",
    title: "Keeping Your Passwords Safe (Without the Headache)",
    excerpt:
      "You don't need to memorize dozens of passwords. Here's a simple, safe way to manage them.",
    date: "May 20, 2026",
    category: "Safety",
    image:
      "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    body: [
      "Passwords protect your money, your photos, and your private messages. But keeping track of them can feel overwhelming. Here's the gentle version.",
      "Use a few strong passwords, not one weak one everywhere. A strong password is simply three random words joined together, like 'river-lamp-coffee'.",
      "Never share a password by email or over the phone. A real company will never ask for it that way.",
      "Consider a password manager — a safe digital notebook that remembers everything for you. We can set one up and show you exactly how it works.",
      "If you're ever locked out, don't panic. Give us a call and we'll help you get back in safely.",
    ],
  },
  {
    slug: "spotting-scams",
    title: "How to Spot a Scam Message Before It Fools You",
    excerpt:
      "The most common warning signs of scam texts, emails, and calls — explained simply.",
    date: "May 6, 2026",
    category: "Safety",
    image:
      "https://images.unsplash.com/photo-1716558057044-72f92e6a5654?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    body: [
      "Scammers try to scare or rush you. The good news: once you know the signs, they're much easier to spot.",
      "Watch for urgency. 'Act now or your account will close!' is a classic scam trick.",
      "Be careful with links. If you didn't expect the message, don't tap the link. When in doubt, call the company directly using a number you trust.",
      "Never give out codes. A bank or company will never call and ask for the code they just texted you.",
      "When something feels off, it usually is. Take a breath, and feel free to call us — we're always glad to take a look with you.",
    ],
  },
  {
    slug: "video-call-grandkids",
    title: "How to Video Call Your Grandkids in 3 Easy Steps",
    excerpt:
      "Seeing your family face-to-face is easier than you think. Let's walk through it together.",
    date: "April 22, 2026",
    category: "Staying Connected",
    image:
      "https://images.unsplash.com/photo-1758686254007-a1aec378eca3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    body: [
      "Video calls are one of the loveliest things technology can do — and they're simpler than they look.",
      "Step 1: Open your video app. On an iPhone or iPad, that's FaceTime. On Android, it's often Google Meet or WhatsApp.",
      "Step 2: Choose the person you want to call from your contacts.",
      "Step 3: Tap the video camera button and smile — you're connected!",
      "If your family uses a different app than you, we can help make sure everyone can reach each other easily.",
    ],
  },
];


// Keyword synonyms for the voice "Find Help" search, keyed by service slug.
export const HELP_KEYWORDS = {
  "phone-setup": [
    "phone", "iphone", "android", "smartphone", "cell", "mobile", "new phone",
    "contacts", "photos", "pictures", "transfer", "text", "texts", "messages",
    "apps", "app", "switch phones", "old phone",
  ],
  "computer-setup": [
    "computer", "laptop", "pc", "mac", "macbook", "windows", "desktop", "chromebook",
    "files", "documents", "unbox", "new computer", "slow computer", "updates",
  ],
  "wifi-network": [
    "wifi", "wi-fi", "wi‑fi", "internet", "router", "modem", "network", "connection",
    "no internet", "slow internet", "dead zone", "signal", "wireless", "hotspot",
  ],
  "smart-home": [
    "ring", "doorbell", "camera", "cameras", "security", "smart home", "alexa",
    "echo", "google home", "nest", "speaker", "thermostat", "smart plug", "smart light",
    "smart tv", "streaming", "roku",
  ],
  "printer-setup": [
    "printer", "print", "printing", "scan", "scanner", "scanning", "ink", "toner",
    "paper jam", "jam", "wireless printer", "won't print", "cant print",
  ],
  "email-passwords": [
    "email", "e-mail", "password", "passwords", "login", "log in", "sign in",
    "locked out", "account", "gmail", "outlook", "yahoo", "reset password",
    "hacked", "forgot password", "two factor", "verification code",
  ],
  "tech-lessons": [
    "lesson", "lessons", "learn", "teach", "coaching", "tutorial", "how to",
    "video call", "facetime", "zoom", "whatsapp", "one on one", "confused",
    "don't understand", "help learning", "basics",
  ],
};

// Normalize text: lowercase + strip punctuation (so "won't" == "wont") + collapse spaces.
const _normalize = (s) =>
  (s || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();

// Bounded Levenshtein edit distance (for light typo tolerance).
const _editDistance = (a, b) => {
  const m = a.length, n = b.length;
  if (Math.abs(m - n) > 2) return 3;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
  }
  return dp[m][n];
};

// Generic words that add noise rather than disambiguating a service.
const _STOPWORDS = new Set([
  "the", "and", "for", "with", "was", "get", "got", "getting", "help", "need",
  "needs", "want", "please", "can", "cant", "cannot", "wont", "work", "working",
  "works", "broken", "break", "fix", "fixing", "fixed", "use", "using", "used",
  "how", "why", "what", "when", "not", "its", "this", "that", "have", "has",
  "having", "some", "any", "from", "your", "you", "our", "out", "off", "new", "old",
]);

// Fuzzy scoring search over services for the "Find Help" feature.
// Tolerates punctuation ("won't" vs "wont"), plurals/substrings, and small typos ("printr").
export const searchHelp = (query) => {
  const nq = _normalize(query);
  if (!nq) return [];
  const tokens = nq.split(" ").filter((t) => t.length > 2 && !_STOPWORDS.has(t));
  return SERVICES.map((s) => {
    const hayText = _normalize(`${s.title} ${s.short} ${(HELP_KEYWORDS[s.slug] || []).join(" ")}`);
    const hayWords = hayText.split(" ");
    let score = 0;
    if (hayText.includes(nq)) score += 6;
    for (const t of tokens) {
      if (hayText.includes(t)) {
        score += 2;
        continue;
      }
      const tol = t.length >= 6 ? 2 : 1;
      if (hayWords.some((w) => Math.abs(w.length - t.length) <= tol && _editDistance(t, w) <= tol)) {
        score += 1;
      }
    }
    return { ...s, score };
  })
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score);
};
